import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { add } from 'date-fns';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs';

import { SharedAuthFacade } from '@lockton-apps/shared/data-access/auth';
import { IdleDialogComponent } from '@lockton-apps/shared/ui/lui';
import { UserIdleService } from '@lockton-apps/shared/util/user-idle';

@Injectable()
export class AuthUserIdleService {
  private timer$!: Observable<number | null>;
  private idleDialog!: MatDialogRef<IdleDialogComponent> | null;

  constructor(
    private userIdleService: UserIdleService,
    private dialog: MatDialog,
    private authFacade: SharedAuthFacade
  ) {}

  init(template: TemplateRef<unknown>) {
    this.userIdleService.startWatching();
    this.authFacade.isAuthenticated$
      .pipe(distinctUntilChanged())
      .subscribe((isAuth) => {
        isAuth
          ? this._startKeepAlive(template)
          : this.userIdleService.stopWatching();
      });
  }

  private _startKeepAlive(template: TemplateRef<unknown>): void {
    this.timer$ = this.userIdleService.onTimerStart().pipe(shareReplay(1));
    this.userIdleService.startWatching();

    this.timer$.pipe(takeUntil(this._loggedOut())).subscribe((v) => {
      if (!this.idleDialog && v !== null) {
        const date = add(new Date(), {
          seconds: this.userIdleService.getConfigValue().timeout,
        });

        this.authFacade.timeoutWarning();

        this.idleDialog = this.dialog.open(IdleDialogComponent, {
          data: {
            contentTemplate: template,
            contentTemplateContext: date,
          },
        });
        this.idleDialog
          .beforeClosed()
          .pipe(take(1))
          .subscribe(() => {
            this.userIdleService.resetTimer();
            this.idleDialog = null;
          });
      }
    });

    this.userIdleService
      .onTimerEnd()
      .pipe(shareReplay(1))
      .subscribe(() => {
        if (this.idleDialog) {
          this.idleDialog.close();
        }
      });

    this.userIdleService.ping$
      .pipe(takeUntil(this._loggedOut()))
      .subscribe(() => {
        this.authFacade.refreshToken();
      });

    this._handleOnTimeout();
  }

  private _handleOnTimeout() {
    this.userIdleService
      .onTimeout()
      .pipe(first())
      .subscribe(() => {
        this.userIdleService.stopWatching();
        this.authFacade.logout();

        this.idleDialog?.close();
      });
  }

  private _loggedOut(): Observable<void> {
    return this.authFacade.isAuthenticated$.pipe(
      filter((isAuthenticated) => !isAuthenticated),
      map(() => undefined)
    );
  }
}
